import React, { useState, useEffect } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import ProductCard from "./ProductCard";
import baseURL from "../utils/BaseURL";

const fetchCategoryProducts = async (categoryId, page) => {
  const { data } = await axios.get(
    `${baseURL}/category_products_page/${categoryId}?page=${page}&size=12`,
  );
  if (!data.success) throw new Error("Failed to fetch products");
  return data.result;
};

const ProductGrid = ({ categoryId, categoryName }) => {
  const [page, setPage] = useState(1);

  // Reset to page 1 whenever the category changes
  useEffect(() => {
    setPage(1);
  }, [categoryId]);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ["categoryProducts", categoryId, page],
    queryFn: () => fetchCategoryProducts(categoryId, page),
    enabled: !!categoryId, // Prevents fetching until ID is set
    staleTime: 1000 * 60 * 5,
  });

  if (!categoryId) return null;

  // Calculate pagination details mathematically
  const total = data?.pagination?.total || 0;
  const perPage = data?.pagination?.per_page || 10;
  const currentPage = data?.pagination?.current_page || 1;

  const showingFrom = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const showingTo = Math.min(currentPage * perPage, total);

  return (
    <div className="flex flex-col h-full ">
      {/* Top Information Bar */}
      <div className="bg-white rounded-2xl p-5 mb-6 flex flex-col sm:flex-row justify-between items-center shadow-sm border border-gray-100 min-h-[5rem]">
        {isLoading || isFetching ? (
          <>
            <div className="h-7 bg-gray-200 rounded-lg w-48 animate-pulse mb-3 sm:mb-0"></div>
            <div className="h-5 bg-gray-100 rounded-lg w-40 animate-pulse"></div>
          </>
        ) : (
          <>
            <h1 className="text-xl md:text-2xl font-bold text-[#15363A]">
              {categoryName}{" "}
              <span className="text-[#998147] font-medium text-lg md:text-xl ml-1">
                Products
              </span>
            </h1>
            <p className="text-gray-500 text-sm md:text-base font-medium mt-2 sm:mt-0">
              Showing{" "}
              <span className="text-[#15363A] font-bold">{showingFrom}</span>–
              <span className="text-[#15363A] font-bold">{showingTo}</span> of{" "}
              <span className="text-[#15363A] font-bold">{total}</span> results
            </p>
          </>
        )}
      </div>

      {/* Loading State for Grid */}
      {isLoading || isFetching ? (
        <div className="w-full py-24 flex flex-col justify-center items-center gap-4 flex-grow">
          <div className="w-12 h-12 border-4 border-[#998147] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : isError ? (
        <div className="text-red-500 py-10 text-center flex-grow font-medium">
          Oops! Failed to load products for this category.
        </div>
      ) : (
        <>
          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 flex-grow">
            {data?.products?.length > 0 ? (
              data.products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))
            ) : (
              <div className="col-span-full py-20 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                </div>
                <p className="text-gray-500 text-lg font-medium">
                  No products found in this category.
                </p>
              </div>
            )}
          </div>

          {/* Numbered Pagination Controls */}
          {data?.pagination && data.pagination.last_page > 1 && (
            <div className="mt-12 flex justify-center items-center gap-2 md:gap-3">
              {/* Previous Button */}
              <button
                onClick={() => setPage((old) => Math.max(old - 1, 1))}
                disabled={page === 1}
                className="p-2.5 rounded-xl bg-white border border-gray-200 text-[#15363A] hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                aria-label="Previous Page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-2">
                {[...Array(data.pagination.last_page)].map((_, index) => {
                  const pageNumber = index + 1;
                  const isActive = page === pageNumber;

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center font-bold transition-all duration-300 ${
                        isActive
                          ? "bg-[#15363A] text-white shadow-lg shadow-[#15363A]/20 scale-110"
                          : "bg-white border border-gray-100 text-gray-500 hover:border-[#998147] hover:text-[#998147]"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>

              {/* Next Button */}
              <button
                onClick={() =>
                  setPage((old) =>
                    !data || old === data.pagination.last_page ? old : old + 1,
                  )
                }
                disabled={page === data.pagination.last_page}
                className="p-2.5 rounded-xl bg-white border border-gray-200 text-[#15363A] hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                aria-label="Next Page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductGrid;
